/**
 * Asset Location Hooks
 * ====================
 * React Query hooks for IT asset location operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { assetLocationApi } from '@/services/api'
import type { AssetLocationCreate, AssetLocationUpdate } from '@/types'

// Query keys
export const assetLocationKeys = {
  all: ['assetLocations'] as const,
  lists: () => [...assetLocationKeys.all, 'list'] as const,
  list: () => [...assetLocationKeys.lists()] as const,
  details: () => [...assetLocationKeys.all, 'detail'] as const,
  detail: (id: number) => [...assetLocationKeys.details(), id] as const,
}

/**
 * Hook to fetch all asset locations with asset counts.
 */
export function useAssetLocations() {
  return useQuery({
    queryKey: assetLocationKeys.list(),
    queryFn: () => assetLocationApi.list(),
  })
}

/**
 * Hook to fetch a single asset location.
 */
export function useAssetLocation(id: number) {
  return useQuery({
    queryKey: assetLocationKeys.detail(id),
    queryFn: () => assetLocationApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new asset location.
 */
export function useCreateAssetLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AssetLocationCreate) => assetLocationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetLocationKeys.lists() })
    },
  })
}

/**
 * Hook to update an asset location.
 */
export function useUpdateAssetLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AssetLocationUpdate }) =>
      assetLocationApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: assetLocationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetLocationKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete an asset location.
 */
export function useDeleteAssetLocation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => assetLocationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetLocationKeys.lists() })
    },
  })
}
